import {
  ColumnsType,
  DeleteIcon,
  InfiniteTable,
  InputDecimal,
} from '@/components/_common';
import { getTableScrollX } from '@/utils/table';

import { useIntl } from 'react-intl';
import { ListStockPartsDeliveryDataSupport } from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';
import { Col, Form, Typography } from 'antd';
import { useDefaultEmptyFieldError } from '@/hooks';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';

interface Props {
  selectedItems: ListStockPartsDeliveryDataSupport[];
  setDeliveredItems: (state: ListStockPartsDeliveryDataSupport[]) => void;
}

const PartsAndConsumablesList: React.FC<Props> = ({
  selectedItems,
  setDeliveredItems,
}) => {
  const intl = useIntl().formatMessage;

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const columns: ColumnsType<ListStockPartsDeliveryDataSupport> = [
    {
      dataIndex: 'workOrderNumber',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.os',
      }),
      ellipsis: true,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'description',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.description',
      }),
      ellipsis: true,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    useConfigurationColumnsPart({ isSku: true }),
    {
      dataIndex: 'licensePlate',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.board',
      }),
      render: (value) => (value ? value : '-'),
      ellipsis: true,
    },
    {
      dataIndex: ['subArea', 'name'],
      title: 'Área',
      ellipsis: true,
      render: (value, row, index) =>
        row.provided ? (
          '-'
        ) : (
          <>
            <Typography.Text>{value}</Typography.Text>
            <Form.Item
              hidden
              noStyle
              name={[index, 'workOrderItemId']}
              initialValue={row.workOrderId}
            />

            <Form.Item
              hidden
              name={[index, 'subareaId']}
              noStyle
              initialValue={row.subArea?.id}
            />
          </>
        ),
    },
    {
      dataIndex: 'costPrice',
      title: 'Preço de Custo',
      ellipsis: true,
      render: (_, row, index) =>
        row.provided ? (
          '-'
        ) : (
          <Col>
            <Form.Item
              hidden
              noStyle
              name={[index, 'complementId']}
              initialValue={
                row.provided ? row.workOrderComplementId : row.complementId
              }
            />
            <Form.Item
              hidden
              noStyle
              name={[index, 'complementId']}
              initialValue={row.provided ? row.workOrderItemId : undefined}
            />

            <Form.Item
              name={[index, 'costPrice']}
              initialValue={row.prices ?? 0}
              style={{ margin: 0, padding: 0 }}
              rules={[defaultEmptyFieldError]}
            >
              <InputDecimal fractions={4} />
            </Form.Item>
          </Col>
        ),
    },
    {
      dataIndex: 'requestedQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.requestedQuantity',
      }),
      ellipsis: true,
      render: (value) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
    {
      dataIndex: 'availableQuantity',
      title: 'Qtde. Disponível',
      ellipsis: true,
      render: (value) => (value ? quantityFormatter(+value) : '-'),
    },
    {
      dataIndex: 'deliveredQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.deliveryQuantity',
      }),
      ellipsis: true,
      render: (_, row, index) => (
        <Col>
          <Form.Item
            initialValue={row.availableQuantity ?? 0}
            name={[index, 'deliveredQuantity']}
            noStyle
          >
            <InputDecimal
              max={row?.isConsumableNoLink ? undefined : row.availableQuantity}
            />
          </Form.Item>
        </Col>
      ),
    },
    {
      title: intl({
        id: 'commons.list.header.action',
      }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (record) => (
        <DeleteIcon
          onClick={() => {
            selectedItems
              ? setDeliveredItems(
                  selectedItems?.filter(
                    (part) => part.workOrderItemId !== record.workOrderItemId,
                  ) ?? [],
                )
              : [];
          }}
        />
      ),
    },
  ];

  return (
    <Form.List name="deliveredItems" initialValue={[]}>
      {() => (
        <InfiniteTable<ListStockPartsDeliveryDataSupport>
          size="middle"
          loading={false}
          rowKey={(row) => row.workOrderItemId}
          columns={columns}
          dataSource={selectedItems}
          scroll={{
            x: getTableScrollX(columns),
          }}
        />
      )}
    </Form.List>
  );
};

export default PartsAndConsumablesList;
